import { Fragment } from '@tiptap/pm/model';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import {
  buildBodyParagraphAttrs,
  resolveIndentForInsertAfterBlock,
} from './post_block_indent.js';
import { buildNormalizedBlockquoteContent } from './pathstone_blockquote_content.js';
import {
  findResourceRowDepth,
  findResourceTableDepth,
  isAtResourceRowTitleStart,
  isResourceRowNode,
  paragraphRowRole,
  resolveIndentAboveResourceTable,
  resourceRowTitleParagraph,
} from './resource_row_paragraph.js';

function findAncestorDepth($from, nodeName) {
  for (let d = $from.depth; d > 0; d--) {
    if ($from.node(d).type.name === nodeName) return d;
  }
  return -1;
}

/** True when the caret is at the beginning of the paragraph's inline content. */
export function isAtParagraphContentStart($from) {
  if ($from.parent.type.name !== 'paragraph') return false;
  return $from.parentOffset === 0;
}

function replaceResourceContainerWithParagraph(view, $from, paragraphNode) {
  const tableDepth = findResourceTableDepth($from);
  const rowDepth = findResourceRowDepth($from);

  let tr = view.state.tr;
  let cursorPos;

  if (tableDepth >= 0) {
    const table = $from.node(tableDepth);
    const tablePos = $from.before(tableDepth);
    tr = tr.replaceWith(tablePos, tablePos + table.nodeSize, paragraphNode);
    cursorPos = tablePos + 1;
  } else if (rowDepth >= 0) {
    const row = $from.node(rowDepth);
    const rowPos = $from.before(rowDepth);
    tr = tr.replaceWith(rowPos, rowPos + row.nodeSize, paragraphNode);
    cursorPos = rowPos + 1;
  } else {
    return false;
  }

  tr.setSelection(TextSelection.create(tr.doc, cursorPos));
  view.dispatch(tr.scrollIntoView());
  return true;
}

function unwrapResourceRowNodeSelection(view, selection) {
  const rowNode = selection.node;
  const rowPos = selection.from;
  const titlePara = resourceRowTitleParagraph(rowNode);
  if (!titlePara) return false;

  const paragraphType = view.state.schema.nodes.paragraph;
  if (!paragraphType) return false;

  const $row = view.state.doc.resolve(rowPos);
  const tableDepth = findResourceTableDepth($row);
  const tablePos = tableDepth >= 0 ? $row.before(tableDepth) : -1;
  const $atTable =
    tablePos >= 0 ? view.state.doc.resolve(tablePos + 1) : $row;
  const indent = resolveIndentAboveResourceTable($atTable, tableDepth);
  const plainPara = paragraphType.create(buildBodyParagraphAttrs(indent), titlePara.content);
  return replaceResourceContainerWithParagraph(view, $row, plainPara);
}

/** Unwrap a resource table (or bare row) into a body paragraph. */
export function tryUnwrapResourceTableAtParagraphStart(view) {
  const { state } = view;
  const { selection } = state;

  if (selection instanceof NodeSelection && isResourceRowNode(selection.node)) {
    return unwrapResourceRowNodeSelection(view, selection);
  }

  if (!selection.empty) return false;

  const { $from } = selection;
  if (!isAtParagraphContentStart($from)) return false;

  const rowDepth = findResourceRowDepth($from);
  if (rowDepth < 0 || $from.depth !== rowDepth + 1) return false;
  if (paragraphRowRole($from.parent) === 'subtitle') return false;
  // Only unwrap from the first line in the row (not gap / staging lines below the title).
  if ($from.index(rowDepth) !== 0) return false;
  if (isAtResourceRowTitleStart($from)) return false;

  const paragraphType = state.schema.nodes.paragraph;
  if (!paragraphType) return false;

  const tableDepth = findResourceTableDepth($from);
  const tablePos = tableDepth >= 0 ? $from.before(tableDepth) : -1;
  const $atTable =
    tablePos >= 0 ? state.doc.resolve(tablePos + 1) : $from;
  const indent = resolveIndentAboveResourceTable($atTable, tableDepth);
  const plainPara = paragraphType.create(buildBodyParagraphAttrs(indent), $from.parent.content);
  return replaceResourceContainerWithParagraph(view, $from, plainPara);
}

/** Unwrap a blockquote into one body paragraph per quote line (plus attribution when present). */
export function tryUnwrapBlockquoteAtParagraphStart(view) {
  const { state } = view;
  const { selection } = state;
  if (!selection.empty) return false;

  const { $from } = selection;
  if (!isAtParagraphContentStart($from)) return false;

  const blockquoteDepth = findAncestorDepth($from, 'blockquote');
  if (blockquoteDepth < 0 || $from.depth !== blockquoteDepth + 1) return false;
  if ($from.parent.attrs?.psQuoteRole === 'attribution') return false;
  if ($from.index(blockquoteDepth) !== 0) return false;

  const blockquote = $from.node(blockquoteDepth);
  const blockquotePos = $from.before(blockquoteDepth);
  const paragraphType = state.schema.nodes.paragraph;
  if (!paragraphType) return false;

  const normalized = buildNormalizedBlockquoteContent(blockquote, state.schema);
  if (!normalized?.length) return false;

  const $atBlockquote = state.doc.resolve(blockquotePos + 1);
  const indent = resolveIndentForInsertAfterBlock($atBlockquote, blockquoteDepth);
  const plainParagraphs = normalized.map((node) =>
    paragraphType.create(buildBodyParagraphAttrs(indent), node.content)
  );

  const tr = state.tr.replaceWith(
    blockquotePos,
    blockquotePos + blockquote.nodeSize,
    Fragment.from(plainParagraphs)
  );
  tr.setSelection(TextSelection.create(tr.doc, blockquotePos + 1));
  view.dispatch(tr.scrollIntoView());
  return true;
}
